import React from 'react';
import { BarChart3, Package, AlertTriangle, Lightbulb, ChevronRight } from 'lucide-react';
import { getGradeFromScore } from '../analysis/scores';
import { getGradeStyles, countGaps } from './utils';
import type { AnalysisJob, PillarScore } from './types';

interface ScoreSummaryProps {
  latestAnalysis: AnalysisJob;
  actionCount?: number;
}

export const ScoreSummary: React.FC<ScoreSummaryProps> = ({
  latestAnalysis,
  actionCount: actionCountProp,
}) => {
  const analysis = latestAnalysis.analysis;
  if (!analysis || analysis.score === undefined) return null;

  const gradeStyles = getGradeStyles(analysis.score);
  const grade = getGradeFromScore(analysis.score);

  const maintenanceScore = analysis.maintenanceScore?.overallScore;
  const depPillar = analysis.maintenanceScore?.pillarScores?.find(
    (p: PillarScore) => p.name === 'dependency' || p.name === 'dependencies',
  );
  const gapCount = countGaps(analysis.gaps);
  const actionCount = actionCountProp ?? analysis.recommendations?.length ?? 0;

  return (
    <section className="mb-6">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Hero SDLC score card */}
        <div
          className={`lg:col-span-2 ${gradeStyles.bg} ${gradeStyles.border} border rounded-xl p-6 flex flex-col justify-between`}
        >
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
              SDLC Score
            </span>
            <BarChart3 className={`w-5 h-5 ${gradeStyles.text}`} />
          </div>
          <div className="flex items-baseline gap-3">
            <span className={`text-5xl font-bold data-value ${gradeStyles.text}`}>
              {analysis.score}
            </span>
            <span className={`text-2xl font-semibold ${gradeStyles.text}`}>{grade}</span>
          </div>
          <a
            href={`/jobs/${latestAnalysis.jobId}/results`}
            className="inline-flex items-center gap-1 text-sm font-medium text-teal-600 dark:text-teal-400 hover:underline mt-4"
          >
            View Report
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>

        {/* Stat cards */}
        <div className="lg:col-span-3 space-y-4">
          {/* Maintenance + Dep Health row (when available) */}
          {(maintenanceScore !== undefined || depPillar) && (
            <div
              className={`grid gap-4 ${maintenanceScore !== undefined && depPillar ? 'grid-cols-2' : 'grid-cols-1'}`}
            >
              {maintenanceScore !== undefined && (
                <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      Maintenance
                    </span>
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-4xl font-bold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {maintenanceScore}
                    </span>
                    <span
                      className="text-xl font-semibold"
                      style={{ color: 'var(--color-text-tertiary)' }}
                    >
                      {analysis.maintenanceScore?.grade}
                    </span>
                  </div>
                </div>
              )}
              {depPillar && (
                <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className="text-sm font-medium"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      Dep. Health
                    </span>
                    <Package className="w-5 h-5 text-purple-500" />
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span
                      className="text-4xl font-bold"
                      style={{ color: 'var(--color-text-primary)' }}
                    >
                      {depPillar.score}
                    </span>
                    <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>
                      /100
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Gaps + Actions row (always present) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Gaps
                </span>
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              </div>
              <span className="text-4xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {gapCount}
              </span>
            </div>
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-slate-700/50 rounded-xl p-5">
              <div className="flex items-center justify-between mb-3">
                <span
                  className="text-sm font-medium"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  Actions
                </span>
                <Lightbulb className="w-5 h-5 text-emerald-500" />
              </div>
              <span className="text-4xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {actionCount}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
